import { ChangeEvent, useEffect, useState } from "react";
import OwLabel from "../OwLabel/OwLabel";
import OwSelectOption from "../OwSelectOption/OwSelectOption";
import Typeahead from "../Typeahead/Typeahead";
import { OwFnInput } from "../OwFnInput/OwFnInput";
import OwButton from "../OwButton/OwButton";
import ICatalog from "../../../models/ICatalog";
import ICatalog_Item from "../../../models/ICatalog_Item";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import EndpointsEnum from "../../../models/EndpointsEnum";
import IGetCatalogsDTO from "../../../models/DTO/IGetCatalogsDTO";
import IOnGetCatalogsDTO from "../../../models/DTO/IOnGetCatalogsDTO";
import ITypeaheadCatalogItemDTO from "../../../models/DTO/ITypeaheadCatalogItemDTO";
import IOnTypeaheadCatalogItemDTO from "../../../models/DTO/IOnTypeaheadCatalogItemDTO";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    onAdd: (item: ICatalog_Item, par: number) => void;
};

/**
 * Inline "add a SKU" row for a par grid (catalog dropdown -> item typeahead -> par).
 * Mirrors TransferDetailAddItemRow, but writes nothing itself: it hands the picked item and par
 * to the parent, which stages a row until the grid's single Save. Lays out with flex-wrap so it
 * adapts to its container (page or dock) rather than the viewport.
 */
export const ParLevelAddItemRow = (props: Props) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();

    const [catalogs, setCatalogs] = useState<ICatalog[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<ICatalog | null>(null);
    const [selectedItem, setSelectedItem] = useState<ICatalog_Item | null>(null);
    const [foundCatalogItems, setFoundCatalogItems] = useState<ICatalog_Item[]>([]);
    const [clearTypeahead, setClearTypeahead] = useState<boolean>(false);
    const [par, setPar] = useState<string>("0");

    const parValue = Number(par);
    const isParValid = par.trim() !== "" && Number.isFinite(parValue) && parValue >= 0;
    const canAdd = !!selectedCatalog && !!selectedItem && isParValid;

    const clearTypeaheadFn = () => {
        setClearTypeahead(false);
        setFoundCatalogItems([]);
    };

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
        };

        socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogs(data.catalogs);

        // Auto-select when there's only one catalog so the user can go straight to the item search.
        if (data.catalogs.length === 1) {
            setSelectedCatalog(data.catalogs[0]);
        }
    };

    const getCatalogItem = (name: string) => {
        if (selectedCatalog) {
            const payload: ITypeaheadCatalogItemDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                catalogGuid: selectedCatalog.guid,
                name,
                options: {
                    searchName: true,
                    searchDescription: true,
                    searchSku: true,
                    searchUpc: true,
                },
            };

            socket.emit(EndpointsEnum.CatalogItemTypeaheadCatalogItem, payload);
        }
    };

    const onGetCatalogItem = (data: IOnTypeaheadCatalogItemDTO) => {
        setFoundCatalogItems(data.catalog_items);
    };

    const sockets: IUiSocketSetup[] = [
        { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
        { endpoint: EndpointsEnum.CatalogItemOnTypeaheadCatalogItem, fn: onGetCatalogItem },
    ];

    useEffect(() => {
        for (const socketSetup of sockets) {
            socket.on(socketSetup.endpoint, socketSetup.fn);
        }

        getCatalogs();

        return () => {
            for (const socketSetup of sockets) {
                socket.removeListener(socketSetup.endpoint, socketSetup.fn);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        switch (name) {
            case "catalog": {
                const catalog = catalogs.find((c) => c.guid === value) || null;
                setSelectedCatalog(catalog);
                // Item belongs to a catalog, so any prior selection is no longer valid.
                setSelectedItem(null);
                setClearTypeahead(true);
                setFoundCatalogItems([]);
                break;
            }

            case "par":
                setPar(String(value ?? ""));
                break;
        }
    };

    const selectedItemFn = (item: ICatalog_Item) => {
        setSelectedItem(item);
    };

    const addFn = () => {
        if (!canAdd || !selectedItem) {
            return;
        }

        props.onAdd(selectedItem, parValue);

        setSelectedItem(null);
        setPar("0");
        setClearTypeahead(true);
        setFoundCatalogItems([]);
    };

    return (
        <div className={"ParLevelAddItemRow d-flex flex-wrap align-items-end gap-2"}>
            <div className={"add-field add-catalog"}>
                <OwLabel title={"catalog"} />
                <OwSelectOption
                    fn={handleChange}
                    titleProp={"name"}
                    valueProp={"guid"}
                    options={catalogs}
                    name={"catalog"}
                    selectedOption={selectedCatalog}
                    selectedOptionProp={"guid"}
                />
            </div>

            <div className={"add-field add-item"}>
                <OwLabel title={"item"} />
                <Typeahead
                    isSelectableFn={() => true}
                    typeaheadApiFn={getCatalogItem}
                    titleProp={"name"}
                    subTitleProp={"sku"}
                    typeaheadItems={foundCatalogItems}
                    clearTypeahead={clearTypeahead}
                    clearTypeaheadCallback={clearTypeaheadFn}
                    selectedItemFn={selectedItemFn}
                    disabled={!selectedCatalog}
                />
            </div>

            <div className={"add-field add-par"}>
                <OwLabel title={"par"} />
                <OwFnInput fn={handleChange} name={"par"} value={par} type={"number"} min={0} />
            </div>

            <div>
                <OwButton title={"add"} leftIconClass={"fas fa-plus"} className={"btn-warning"} fn={addFn} disabled={!canAdd} />
            </div>
        </div>
    );
};
